'use client';

import { memo } from 'react';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateForUser } from '@/utils/formatters';
import type { GridSlotRef } from './grid-model';

interface GridAvailableTeamsButtonProps {
  date: string;
  time: string;
  onShowAvailableTeams: (slot: GridSlotRef) => void;
  /** Icon only, for the day view's 72px time gutter, where the text doesn't fit. */
  compact?: boolean;
}

/**
 * Opens "¿Quién puede?" for a slot. Drawn as a tinted chip rather than a bare gray icon, which
 * organizers didn't notice was a button.
 *
 * Memoized, and handed the slot rather than a ready-made closure, because every cell re-renders
 * on each pointer-move tick of a drag (see `GridMatchCardView`): with stable props this skips it.
 */
export const GridAvailableTeamsButton = memo(function GridAvailableTeamsButton({
  date,
  time,
  onShowAvailableTeams,
  compact = false,
}: GridAvailableTeamsButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'flex shrink-0 items-center gap-1 rounded border border-blue-200 bg-blue-50 text-[11px] font-medium text-blue-700 hover:border-blue-300 hover:bg-blue-100',
        compact ? 'p-1' : 'px-1.5 py-0.5',
      )}
      onClick={() => onShowAvailableTeams({ date, time })}
      title="¿Qué parejas pueden jugar en este horario?"
      // The date too: in the weekly view every column of a row has one of these buttons.
      aria-label={`Ver qué parejas pueden jugar el ${formatDateForUser(date, 'EEE dd/MM')} a las ${time}`}
    >
      <Users className="h-3.5 w-3.5" aria-hidden />
      {!compact && '¿Quién puede?'}
    </button>
  );
});
